import { useEffect, useState } from "react";
import axios from "axios";
import { ownerApi, formatDetail } from "../api";
import { formatCT } from "../lib/time";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Layers, ShieldCheck, PlayCircle, Bug } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BootReports() {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [rerunState, setRerunState] = useState({ running: false, result: null });
  const [backfillState, setBackfillState] = useState({ running: false, result: null, confirmOpen: false });
  const [cirState, setCirState] = useState({ running: false, dryRun: null, confirmOpen: false, executeResult: null });
  const [orphanState, setOrphanState] = useState({ running: false, dryRun: null, confirmOpen: false, executeResult: null, scopedTenantId: "" });
  const [debugMode, setDebugMode] = useState({ enabled: null, updated_at: null, updated_by: null, saving: false });

  const loadDebugMode = async () => {
    try {
      const { data } = await ownerApi.get("/debug-mode");
      setDebugMode((s) => ({ ...s, ...data, saving: false }));
    } catch {
      setDebugMode((s) => ({ ...s, saving: false }));
    }
  };

  const toggleDebugMode = async () => {
    if (debugMode.saving) return;
    const next = !debugMode.enabled;
    setDebugMode((s) => ({ ...s, saving: true }));
    try {
      const { data } = await ownerApi.put("/debug-mode", { enabled: next });
      setDebugMode({ ...data, saving: false });
      // Broadcast so OwnerLayout's persistent banner reflects the change
      // immediately instead of waiting for its 30s poll.
      window.dispatchEvent(new CustomEvent("owner:debug-mode-changed", { detail: { enabled: !!data.enabled } }));
    } catch {
      setDebugMode((s) => ({ ...s, saving: false }));
    }
  };

  const load = async () => {
    try {
      const { data } = await ownerApi.get("/platform/boot-reports?limit=50");
      setRows(data);
    } catch {
      setRows([]);
    }
    try {
      const { data: h } = await axios.get(`${BACKEND_URL}/api/health/ready`, { withCredentials: false });
      setHealth(h);
    } catch {
      setHealth({ error: true });
    }
  };

  const rerunMigration = async () => {
    if (rerunState.running) return;
    setRerunState({ running: true, result: null });
    try {
      const { data } = await ownerApi.post("/platform/rerun-migration");
      setRerunState({ running: false, result: { ok: true, data } });
    } catch (err) {
      const detail = formatDetail(err?.response?.data?.detail) || err.message;
      setRerunState({ running: false, result: { ok: false, error: detail, status: err?.response?.status } });
    }
    // Refresh health + boot reports regardless of outcome — the attempt itself
    // will have written a boot_report row via the backend hook.
    await load();
  };

  const runBackfill = async () => {
    if (backfillState.running) return;
    setBackfillState((s) => ({ ...s, running: true, result: null, confirmOpen: false }));
    try {
      const { data } = await ownerApi.post("/platform/backfill-lexp-scores");
      setBackfillState({ running: false, result: { ok: true, data }, confirmOpen: false });
    } catch (err) {
      const detail = formatDetail(err?.response?.data?.detail) || err.message;
      setBackfillState({
        running: false,
        result: { ok: false, error: detail, status: err?.response?.status },
        confirmOpen: false,
      });
    }
  };

  // Phase 6 Step 3 CIR→LIR consolidation. Two-phase: dry_run first, then
  // execute with the CONSOLIDATE token.
  const runCirDryRun = async () => {
    if (cirState.running) return;
    setCirState((s) => ({ ...s, running: true, dryRun: null, executeResult: null, confirmOpen: false }));
    try {
      const { data } = await ownerApi.post("/platform/consolidate-cir", { dry_run: true });
      setCirState((s) => ({ ...s, running: false, dryRun: { ok: true, data } }));
    } catch (err) {
      const detail = formatDetail(err?.response?.data?.detail) || err.message;
      setCirState((s) => ({ ...s, running: false, dryRun: { ok: false, error: detail, status: err?.response?.status } }));
    }
  };

  const runCirExecute = async () => {
    if (cirState.running) return;
    setCirState((s) => ({ ...s, running: true, executeResult: null, confirmOpen: false }));
    try {
      const { data } = await ownerApi.post("/platform/consolidate-cir", {
        dry_run: false,
        confirm_token: "CONSOLIDATE",
      });
      setCirState((s) => ({ ...s, running: false, executeResult: { ok: true, data } }));
    } catch (err) {
      // The endpoint throws 500 with structured detail on parity failure.
      const raw = err?.response?.data?.detail;
      const message = typeof raw === "object" ? raw.message : formatDetail(raw);
      setCirState((s) => ({
        ...s,
        running: false,
        executeResult: {
          ok: false,
          error: message || err.message,
          status: err?.response?.status,
          payload: typeof raw === "object" ? raw.result : undefined,
        },
      }));
    }
  };

  // Orphaned lexp_scores cleanup. Same two-phase pattern as CIR consolidation.
  const runOrphanDryRun = async () => {
    if (orphanState.running) return;
    setOrphanState((s) => ({ ...s, running: true, dryRun: null, executeResult: null, confirmOpen: false }));
    try {
      const body = { dry_run: true };
      if (orphanState.scopedTenantId.trim()) body.tenant_id = orphanState.scopedTenantId.trim();
      const { data } = await ownerApi.post("/platform/cleanup-orphan-lexp-scores", body);
      setOrphanState((s) => ({ ...s, running: false, dryRun: { ok: true, data } }));
    } catch (err) {
      const detail = formatDetail(err?.response?.data?.detail) || err.message;
      setOrphanState((s) => ({ ...s, running: false, dryRun: { ok: false, error: detail, status: err?.response?.status } }));
    }
  };

  const runOrphanExecute = async () => {
    if (orphanState.running) return;
    setOrphanState((s) => ({ ...s, running: true, executeResult: null, confirmOpen: false }));
    try {
      const body = { dry_run: false, confirm_token: "DELETE_ORPHANS" };
      if (orphanState.scopedTenantId.trim()) body.tenant_id = orphanState.scopedTenantId.trim();
      const { data } = await ownerApi.post("/platform/cleanup-orphan-lexp-scores", body);
      setOrphanState((s) => ({ ...s, running: false, executeResult: { ok: true, data } }));
    } catch (err) {
      const detail = formatDetail(err?.response?.data?.detail) || err.message;
      setOrphanState((s) => ({ ...s, running: false, executeResult: { ok: false, error: detail, status: err?.response?.status } }));
    }
  };

  useEffect(() => { load(); loadDebugMode(); }, []);

  return (
    <div className="space-y-6" data-testid="owner-dev-utility-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#f1fcff]">Dev Utility</h1>
          <p className="text-sm text-[#ebeef5]/70 mt-1">One-click scripts, migrations, and boot diagnostics. Run platform maintenance tasks from this console.</p>
        </div>
        <button
          onClick={load}
          className="text-xs font-semibold px-3 py-2 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff] flex items-center gap-1.5"
          data-testid="dev-utility-refresh"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <SliceHealthBadge
        health={health}
        open={healthOpen}
        onToggle={() => setHealthOpen((v) => !v)}
        onRerun={rerunMigration}
        rerunState={rerunState}
      />

      {/* Debug Mode toggle — Owner-only switch that enriches 500 responses
          with the full traceback. Persists in platform_settings. */}
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5" data-testid="debug-mode-tile">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="text-[11px] text-[#ebeef5]/70 max-w-2xl">
            <p className="font-semibold text-sm text-[#f1fcff] mb-1 flex items-center gap-2">
              <Bug className="h-4 w-4 text-amber-400" /> Debug Mode (500 enrichment)
            </p>
            <p>
              When enabled, every <span className="font-mono">500 Internal Server Error</span> response includes a <span className="font-mono">debug</span> block with the exception type, message, full traceback, request path, method, and timestamp. The <span className="font-mono">detail</span> field stays <span className="font-mono">"Internal Server Error"</span> in both modes — existing frontend error handlers are untouched.
            </p>
            <p className="mt-2 text-amber-300/90">
              <span className="font-semibold">Leave OFF in production.</span> Tracebacks reveal internal file paths and code structure to anyone who can trigger a 500.
            </p>
            {debugMode.updated_at && (
              <p className="mt-2 text-[10px] text-[#ebeef5]/50">
                Last toggled <span className="font-mono">{formatCT(debugMode.updated_at)}</span>
                {debugMode.updated_by ? <> by <span className="font-mono">{debugMode.updated_by}</span></> : null}
              </p>
            )}
          </div>
          <button
            onClick={toggleDebugMode}
            disabled={debugMode.enabled === null || debugMode.saving}
            className={`shrink-0 text-xs font-semibold px-4 py-2 rounded-md border flex items-center gap-2 transition ${
              debugMode.enabled
                ? "border-amber-400/60 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20"
                : "border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff] hover:border-white/30"
            } ${debugMode.saving ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="debug-mode-toggle-btn"
          >
            {debugMode.saving ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>
            ) : debugMode.enabled === null ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Loading…</>
            ) : debugMode.enabled ? (
              <><span className="h-2 w-2 rounded-full bg-amber-300 animate-pulse" /> Debug ON · click to disable</>
            ) : (
              <><span className="h-2 w-2 rounded-full bg-[#ebeef5]/30" /> Debug OFF · click to enable</>
            )}
          </button>
        </div>
      </div>

      {/* Re-sync historical LEXP scores — Owner-only one-shot backfill */}
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5" data-testid="lexp-backfill-tile">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="text-[11px] text-[#ebeef5]/70 max-w-2xl">
            <p className="font-semibold text-sm text-[#f1fcff] mb-1">Re-sync historical LEXP scores</p>
            <p>
              Re-runs the per-LEXP score recompute for every (learner, LEXP, cohort) tuple in the database. Idempotent — only updates score fields, never flips finalization state. Use this once after deploying a gradebook fix that changes how scores are calculated, or if a cohort/learner gradebook shows a stale score.
            </p>
          </div>
          <button
            onClick={() => setBackfillState((s) => ({ ...s, confirmOpen: true, result: null }))}
            disabled={backfillState.running}
            className={`shrink-0 text-xs font-semibold px-4 py-2 rounded-md border flex items-center gap-2 transition ${
              backfillState.running
                ? "border-white/10 text-[#ebeef5]/50 cursor-not-allowed"
                : "border-[#10b7c0]/40 text-[#10b7c0] hover:bg-[#10b7c0]/10"
            }`}
            data-testid="lexp-backfill-trigger-btn"
          >
            {backfillState.running
              ? (<><Loader2 className="h-3 w-3 animate-spin" /> Re-syncing…</>)
              : (<><PlayCircle className="h-3 w-3" /> Re-sync scores</>)}
          </button>
        </div>

        {backfillState.result && (
          <div
            className={`mt-3 rounded px-3 py-2 text-xs border ${
              backfillState.result.ok
                ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300"
                : "border-red-500/40 bg-red-500/5 text-red-300"
            }`}
            data-testid="lexp-backfill-result"
          >
            {backfillState.result.ok ? (
              <>
                <p className="font-semibold mb-1">
                  ✓ Re-synced {backfillState.result.data?.recomputed ?? 0} score record{backfillState.result.data?.recomputed === 1 ? "" : "s"}.
                </p>
                <p className="text-[#ebeef5]/70">
                  total_tuples: <span className="font-mono">{backfillState.result.data?.total_tuples ?? 0}</span>
                  {" · "}
                  recomputed: <span className="font-mono text-emerald-300">{backfillState.result.data?.recomputed ?? 0}</span>
                  {" · "}
                  failed: <span className={`font-mono ${backfillState.result.data?.failed ? "text-red-300" : "text-emerald-300"}`}>{backfillState.result.data?.failed ?? 0}</span>
                </p>
                {backfillState.result.data?.failures?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-red-300">View first {backfillState.result.data.failures.length} failure(s)</summary>
                    <pre className="mt-1 whitespace-pre-wrap break-all text-[10px] text-[#ebeef5]/70">
                      {JSON.stringify(backfillState.result.data.failures, null, 2)}
                    </pre>
                  </details>
                )}
              </>
            ) : (
              <>
                <p className="font-semibold mb-1">
                  ✗ Re-sync failed{backfillState.result.status ? ` (HTTP ${backfillState.result.status})` : ""}
                </p>
                <p className="break-all text-[#ebeef5]">{backfillState.result.error}</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {backfillState.confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          onClick={() => setBackfillState((s) => ({ ...s, confirmOpen: false }))}
          data-testid="lexp-backfill-confirm-modal"
        >
          <div
            className="bg-[#0a1530] border border-white/10 rounded-xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-heading font-bold text-[#f1fcff] mb-2">Re-sync historical scores?</h3>
            <p className="text-sm text-[#ebeef5]/70 mb-4">
              This re-runs the per-LEXP score recompute for every (learner, LEXP, cohort) tuple in the database. It's safe and idempotent — finalization state is preserved, only score fields are updated. Larger orgs may take 30–60 seconds.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setBackfillState((s) => ({ ...s, confirmOpen: false }))}
                className="text-xs font-semibold px-4 py-2 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff]"
                data-testid="lexp-backfill-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={runBackfill}
                className="text-xs font-semibold px-4 py-2 rounded-md border border-[#10b7c0]/40 text-[#10b7c0] hover:bg-[#10b7c0]/10 flex items-center gap-2"
                data-testid="lexp-backfill-confirm-btn"
              >
                <PlayCircle className="h-3 w-3" /> Run re-sync
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 6 Step 3 — CIR → LIR consolidation (Owner-only, two-phase) */}
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5" data-testid="cir-consolidate-tile">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="text-[11px] text-[#ebeef5]/70 max-w-2xl">
            <p className="font-semibold text-sm text-[#f1fcff] mb-1 flex items-center gap-2">
              <Layers className="h-4 w-4 text-amber-400" /> Phase 6 Step 3 · CIR → LIR consolidation
            </p>
            <p>
              Merges every row in <span className="font-mono">learner_item_responses</span> into <span className="font-mono">learner_item_responses</span> on key <span className="font-mono">(tenant_id, learner_id, lexp_id, item_id)</span>. Field map: <span className="font-mono">is_correct → correct</span>, <span className="font-mono">point_value → max_points</span>, stamps <span className="font-mono">response_origin="manual"</span>. Conflict: latest <span className="font-mono">updated_at</span> wins, <span className="font-mono">entered_at</span> tiebreaker. Source renamed to <span className="font-mono">_archive_v1</span> only after parity passes.
            </p>
            <p className="mt-2 text-amber-300/90">
              <span className="font-semibold">Always dry-run first.</span> Review the classification block — net-new count, conflicts, invalid-key — before clicking Execute.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={runCirDryRun}
              disabled={cirState.running}
              className={`text-xs font-semibold px-4 py-2 rounded-md border flex items-center gap-2 transition ${
                cirState.running
                  ? "border-white/10 text-[#ebeef5]/40 cursor-not-allowed"
                  : "border-white/10 text-[#ebeef5]/80 hover:text-[#f1fcff] hover:border-white/30"
              }`}
              data-testid="cir-consolidate-dryrun-btn"
            >
              {cirState.running ? <Loader2 className="h-3 w-3 animate-spin" /> : <PlayCircle className="h-3 w-3" />}
              Dry run
            </button>
            <button
              onClick={() => setCirState((s) => ({ ...s, confirmOpen: true }))}
              disabled={cirState.running || !cirState.dryRun?.ok}
              className={`text-xs font-semibold px-4 py-2 rounded-md border flex items-center gap-2 transition ${
                cirState.running || !cirState.dryRun?.ok
                  ? "border-white/10 text-[#ebeef5]/40 cursor-not-allowed"
                  : "border-amber-400/60 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20"
              }`}
              title={!cirState.dryRun?.ok ? "Run dry-run first" : ""}
              data-testid="cir-consolidate-execute-btn"
            >
              <PlayCircle className="h-3 w-3" /> Execute
            </button>
          </div>
        </div>

        {cirState.dryRun && (
          <div
            className={`mt-4 text-[11px] rounded-md p-3 ${
              cirState.dryRun.ok
                ? "bg-white/5 border border-white/10 text-[#ebeef5]"
                : "bg-red-500/10 border border-red-500/30 text-red-300"
            }`}
            data-testid="cir-consolidate-dryrun-result"
          >
            {cirState.dryRun.ok ? (() => {
              const d = cirState.dryRun.data || {};
              if (d.already_consolidated) {
                return (
                  <p className="font-semibold">✓ Already consolidated — {d.message}</p>
                );
              }
              const c = d.counts || {};
              const cls = d.classification || {};
              return (
                <>
                  <p className="font-semibold mb-1">✓ Dry run classified {d.per_row_total} source rows</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                    <div><span className="text-[#ebeef5]/60">CIR before:</span> <span className="font-mono">{c.cir_before}</span></div>
                    <div><span className="text-[#ebeef5]/60">LIR before:</span> <span className="font-mono">{c.lir_before}</span></div>
                    <div><span className="text-[#ebeef5]/60">Projected LIR after:</span> <span className="font-mono">{c.projected_lir_after}</span></div>
                    <div><span className="text-[#ebeef5]/60">Invalid key:</span> <span className={`font-mono ${cls.invalid_key ? "text-red-300" : ""}`}>{cls.invalid_key}</span></div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                    <div><span className="text-[#ebeef5]/60">Net-new:</span> <span className="font-mono text-emerald-300">{cls.net_new}</span></div>
                    <div><span className="text-[#ebeef5]/60">Identical:</span> <span className="font-mono">{cls.already_identical}</span></div>
                    <div><span className="text-[#ebeef5]/60">CIR wins:</span> <span className="font-mono text-amber-300">{cls.cir_wins}</span></div>
                    <div><span className="text-[#ebeef5]/60">LIR wins:</span> <span className="font-mono">{cls.lir_wins}</span></div>
                  </div>
                  {(d.per_row_sample || []).length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-[#ebeef5]/60">View first {d.per_row_sample.length} per-row decisions</summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all text-[10px] text-[#ebeef5]/70">
                        {JSON.stringify(d.per_row_sample, null, 2)}
                      </pre>
                    </details>
                  )}
                </>
              );
            })() : (
              <>
                <p className="font-semibold mb-1">✗ Dry-run failed{cirState.dryRun.status ? ` (HTTP ${cirState.dryRun.status})` : ""}</p>
                <p className="break-all text-[#ebeef5]">{cirState.dryRun.error}</p>
              </>
            )}
          </div>
        )}

        {cirState.executeResult && (
          <div
            className={`mt-4 text-[11px] rounded-md p-3 ${
              cirState.executeResult.ok
                ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200"
                : "bg-red-500/10 border border-red-500/30 text-red-300"
            }`}
            data-testid="cir-consolidate-execute-result"
          >
            {cirState.executeResult.ok ? (() => {
              const d = cirState.executeResult.data || {};
              const a = d.applied || {};
              const p = d.parity_check || {};
              const r = d.rename || {};
              return (
                <>
                  <p className="font-semibold mb-1">✓ Consolidation executed successfully</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div><span className="text-[#ebeef5]/60">Inserted:</span> <span className="font-mono">{a.inserted}</span></div>
                    <div><span className="text-[#ebeef5]/60">Overwritten:</span> <span className="font-mono">{a.overwritten}</span></div>
                    <div><span className="text-[#ebeef5]/60">No-op:</span> <span className="font-mono">{a.noop}</span></div>
                    <div><span className="text-[#ebeef5]/60">Skipped LIR-wins:</span> <span className="font-mono">{a.skipped_lir_wins}</span></div>
                    <div><span className="text-[#ebeef5]/60">Skipped invalid:</span> <span className="font-mono">{a.skipped_invalid}</span></div>
                    <div><span className="text-[#ebeef5]/60">Parity count:</span> <span className="font-mono">{p.count_ok ? "OK" : "MISMATCH"}</span></div>
                  </div>
                  <p className="mt-2">
                    Rename: <span className="font-mono">{r.performed ? "✓ performed" : "✗ skipped"}</span>
                    {r.archive_count != null && <> · archive has <span className="font-mono">{r.archive_count}</span> docs</>}
                  </p>
                </>
              );
            })() : (
              <>
                <p className="font-semibold mb-1">✗ Execution failed{cirState.executeResult.status ? ` (HTTP ${cirState.executeResult.status})` : ""}</p>
                <p className="break-all">{cirState.executeResult.error}</p>
                {cirState.executeResult.payload && (
                  <details className="mt-2">
                    <summary className="cursor-pointer">Full diagnostic payload</summary>
                    <pre className="mt-1 whitespace-pre-wrap break-all text-[10px]">
                      {JSON.stringify(cirState.executeResult.payload, null, 2)}
                    </pre>
                  </details>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* CIR execute confirm modal */}
      {cirState.confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          onClick={() => setCirState((s) => ({ ...s, confirmOpen: false }))}
          data-testid="cir-consolidate-confirm-modal"
        >
          <div
            className="bg-[#0a1530] border border-amber-400/30 rounded-xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-heading font-bold text-[#f1fcff] mb-2">Execute CIR → LIR consolidation?</h3>
            <p className="text-sm text-[#ebeef5]/70 mb-4">
              This will upsert every classified source row into <span className="font-mono">learner_item_responses</span> and, on parity-check success, rename <span className="font-mono">learner_item_responses</span> → <span className="font-mono">_archive_v1</span>. The rename is reversible; LIR upserts are not (take a mongodump first).
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCirState((s) => ({ ...s, confirmOpen: false }))}
                className="text-xs font-semibold px-4 py-2 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff]"
                data-testid="cir-consolidate-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={runCirExecute}
                className="text-xs font-semibold px-4 py-2 rounded-md border border-amber-400/60 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20 flex items-center gap-2"
                data-testid="cir-consolidate-confirm-btn"
              >
                <PlayCircle className="h-3 w-3" /> Execute consolidation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orphaned lexp_scores cleanup (Owner-only, two-phase, tenant-safe) */}
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5" data-testid="orphan-lexp-scores-tile">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="text-[11px] text-[#ebeef5]/70 max-w-2xl">
            <p className="font-semibold text-sm text-[#f1fcff] mb-1 flex items-center gap-2">
              <Layers className="h-4 w-4 text-rose-400" /> Orphaned LEXP scores cleanup
            </p>
            <p>
              Identifies and removes <span className="font-mono">lexp_scores</span> rows whose <span className="font-mono">learner_id</span> is not present in the <span className="font-mono">learners</span> collection (within the same tenant). Typical sources: aborted learner deletions or imports that wrote scores before the matching learner row.
            </p>
            <p className="mt-2 text-rose-300/90">
              <span className="font-semibold">Tenant-safe:</span> every delete is keyed by both <span className="font-mono">(tenant_id, learner_id)</span>. Optional <span className="font-mono">tenant_id</span> field below scopes both audit and writes to a single org. Always dry-run first.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-[#ebeef5]/50">Scope to tenant_id (optional):</label>
              <input
                type="text"
                value={orphanState.scopedTenantId}
                onChange={(e) => setOrphanState((s) => ({ ...s, scopedTenantId: e.target.value }))}
                placeholder="leave blank to scan all tenants"
                className="text-[11px] font-mono px-2 py-1 rounded bg-[#0a0c14] border border-white/10 text-[#ebeef5] w-[26ch] focus:outline-none focus:border-rose-400/50"
                disabled={orphanState.running}
                data-testid="orphan-tenant-id-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={runOrphanDryRun}
              disabled={orphanState.running}
              className={`text-xs font-semibold px-4 py-2 rounded-md border flex items-center gap-2 transition ${
                orphanState.running
                  ? "border-white/10 text-[#ebeef5]/40 cursor-not-allowed"
                  : "border-white/10 text-[#ebeef5]/80 hover:text-[#f1fcff] hover:border-white/30"
              }`}
              data-testid="orphan-lexp-scores-dryrun-btn"
            >
              {orphanState.running ? <Loader2 className="h-3 w-3 animate-spin" /> : <PlayCircle className="h-3 w-3" />}
              Dry run
            </button>
            <button
              onClick={() => setOrphanState((s) => ({ ...s, confirmOpen: true }))}
              disabled={orphanState.running || !orphanState.dryRun?.ok}
              className={`text-xs font-semibold px-4 py-2 rounded-md border flex items-center gap-2 transition ${
                orphanState.running || !orphanState.dryRun?.ok
                  ? "border-white/10 text-[#ebeef5]/40 cursor-not-allowed"
                  : "border-rose-400/60 bg-rose-400/10 text-rose-300 hover:bg-rose-400/20"
              }`}
              title={!orphanState.dryRun?.ok ? "Run dry-run first" : ""}
              data-testid="orphan-lexp-scores-execute-btn"
            >
              <PlayCircle className="h-3 w-3" /> Execute
            </button>
          </div>
        </div>

        {orphanState.dryRun && (
          <div
            className={`mt-4 text-[11px] rounded-md p-3 ${
              orphanState.dryRun.ok
                ? "bg-white/5 border border-white/10 text-[#ebeef5]"
                : "bg-red-500/10 border border-red-500/30 text-red-300"
            }`}
            data-testid="orphan-lexp-scores-dryrun-result"
          >
            {orphanState.dryRun.ok ? (() => {
              const d = orphanState.dryRun.data || {};
              const tenants = Object.values(d.by_tenant || {});
              if (d.orphan_count === 0) {
                return (
                  <p className="font-semibold">
                    ✓ No orphans found{d.scoped_tenant_id ? <> in tenant <span className="font-mono">{d.scoped_tenant_id}</span></> : null}.
                    Scanned <span className="font-mono">{d.total_lexp_scores_scanned}</span> rows.
                  </p>
                );
              }
              return (
                <>
                  <p className="font-semibold mb-1">
                    ⚠ Found <span className="text-rose-300">{d.orphan_count}</span> orphaned row{d.orphan_count === 1 ? "" : "s"} across {tenants.length} tenant{tenants.length === 1 ? "" : "s"}
                    <span className="text-[#ebeef5]/60"> (scanned {d.total_lexp_scores_scanned} total)</span>
                  </p>
                  <div className="space-y-2 mt-2">
                    {tenants.map((t) => (
                      <details key={t.tenant_id || "__null__"} className="bg-[#0a0c14] border border-white/5 rounded p-2">
                        <summary className="cursor-pointer">
                          <span className="font-mono text-[#ebeef5]/80">{t.tenant_id || "(null tenant_id)"}</span>
                          <span className="ml-3 text-rose-300">{t.count} orphan{t.count === 1 ? "" : "s"}</span>
                          <span className="ml-3 text-[#ebeef5]/60">· {t.learner_ids.length} learner{t.learner_ids.length === 1 ? "" : "s"}</span>
                          <span className="ml-3 text-[#ebeef5]/60">· {t.cohort_ids.length} cohort{t.cohort_ids.length === 1 ? "" : "s"}</span>
                        </summary>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-[#ebeef5]/50">Affected learner_ids</p>
                            <pre className="mt-1 text-[10px] font-mono text-[#ebeef5]/80 max-h-32 overflow-auto whitespace-pre-wrap break-all">{t.learner_ids.join("\n")}</pre>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-[#ebeef5]/50">Affected cohort_ids</p>
                            <pre className="mt-1 text-[10px] font-mono text-[#ebeef5]/80 max-h-32 overflow-auto whitespace-pre-wrap break-all">{t.cohort_ids.join("\n")}</pre>
                          </div>
                        </div>
                      </details>
                    ))}
                  </div>
                </>
              );
            })() : (
              <>
                <p className="font-semibold mb-1">✗ Dry-run failed{orphanState.dryRun.status ? ` (HTTP ${orphanState.dryRun.status})` : ""}</p>
                <p className="break-all text-[#ebeef5]">{orphanState.dryRun.error}</p>
              </>
            )}
          </div>
        )}

        {orphanState.executeResult && (
          <div
            className={`mt-4 text-[11px] rounded-md p-3 ${
              orphanState.executeResult.ok
                ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200"
                : "bg-red-500/10 border border-red-500/30 text-red-300"
            }`}
            data-testid="orphan-lexp-scores-execute-result"
          >
            {orphanState.executeResult.ok ? (() => {
              const d = orphanState.executeResult.data || {};
              const entries = Object.entries(d.deleted_by_tenant || {});
              return (
                <>
                  <p className="font-semibold mb-1">✓ Deleted <span className="text-emerald-300">{d.deleted_total}</span> orphaned row{d.deleted_total === 1 ? "" : "s"}</p>
                  {entries.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {entries.map(([tid, n]) => (
                        <div key={tid} className="text-[#ebeef5]/80">
                          <span className="font-mono">{tid}</span> → <span className="font-mono text-emerald-300">{n} row{n === 1 ? "" : "s"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })() : (
              <>
                <p className="font-semibold mb-1">✗ Execution failed{orphanState.executeResult.status ? ` (HTTP ${orphanState.executeResult.status})` : ""}</p>
                <p className="break-all">{orphanState.executeResult.error}</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Orphan execute confirm modal */}
      {orphanState.confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          onClick={() => setOrphanState((s) => ({ ...s, confirmOpen: false }))}
          data-testid="orphan-lexp-scores-confirm-modal"
        >
          <div
            className="bg-[#0a1530] border border-rose-400/30 rounded-xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-heading font-bold text-[#f1fcff] mb-2">Delete orphaned LEXP scores?</h3>
            <p className="text-sm text-[#ebeef5]/70 mb-3">
              This will permanently delete <span className="font-mono text-rose-300">{orphanState.dryRun?.data?.orphan_count ?? 0}</span> row{(orphanState.dryRun?.data?.orphan_count ?? 0) === 1 ? "" : "s"} from <span className="font-mono">lexp_scores</span> across {Object.keys(orphanState.dryRun?.data?.by_tenant || {}).length} tenant{Object.keys(orphanState.dryRun?.data?.by_tenant || {}).length === 1 ? "" : "s"}.
            </p>
            <p className="text-xs text-[#ebeef5]/60 mb-4">
              Deletion is keyed by <span className="font-mono">(tenant_id, learner_id)</span> per orphan. Other tenants are not affected. Action is not reversible — take a mongodump first if unsure.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setOrphanState((s) => ({ ...s, confirmOpen: false }))}
                className="text-xs font-semibold px-4 py-2 rounded-md border border-white/10 text-[#ebeef5]/80 hover:text-[#f1fcff] hover:border-white/30"
                data-testid="orphan-lexp-scores-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={runOrphanExecute}
                className="text-xs font-semibold px-4 py-2 rounded-md border border-rose-400/60 bg-rose-400/10 text-rose-300 hover:bg-rose-400/20 flex items-center gap-2"
                data-testid="orphan-lexp-scores-confirm-btn"
              >
                <PlayCircle className="h-3 w-3" /> Delete orphans
              </button>
            </div>
          </div>
        </div>
      )}

      {rows === null ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#10b7c0]" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-[#0a1530] border border-white/10 rounded-xl p-12 text-center">
          <p className="text-sm text-[#ebeef5]/70">No boot records yet.</p>
          <p className="text-xs text-[#ebeef5]/50 mt-1">Records appear the first time the backend starts after this code is deployed.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const open = openId === r.id;
            const clean = (r.verify_problems || 0) === 0 && !r.stages?.additive_migration_error;
            return (
              <div
                key={r.id}
                className={`bg-[#0a1530] border rounded-xl overflow-hidden ${clean ? "border-white/10" : "border-red-500/40"}`}
                data-testid={`boot-report-row-${r.id}`}
              >
                <button
                  onClick={() => setOpenId(open ? null : r.id)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left"
                >
                  {open ? <ChevronDown className="h-4 w-4 text-[#ebeef5]/70" /> : <ChevronRight className="h-4 w-4 text-[#ebeef5]/70" />}
                  {clean ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-400" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#f1fcff] truncate">
                      {formatCT(r.started_at)}
                      <span className="ml-3 text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/50">
                        db · {r.target_db}
                      </span>
                    </p>
                    <p className="text-xs text-[#ebeef5]/70 mt-0.5">
                      {clean ? "Clean boot" : `${r.verify_problems} issue(s) · ${r.stages?.additive_migration_error ? "migration raised" : "verify problems"}`}
                      {r.counts?.tenant_id_backfilled ? ` · ${r.counts.tenant_id_backfilled} docs backfilled` : ""}
                    </p>
                  </div>
                </button>
                {open && (
                  <div className="px-5 pb-5 border-t border-white/5 pt-4" data-testid={`boot-report-detail-${r.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                      <Pair label="Started" value={formatCT(r.started_at)} />
                      <Pair label="Finished" value={r.finished_at ? formatCT(r.finished_at) : "—"} />
                      <Pair label="Target DB" value={r.target_db} />
                      <Pair label="Verify problems" value={r.verify_problems || 0} />
                    </div>
                    {r.problems?.length > 0 && (
                      <div className="mt-3 bg-red-500/5 border border-red-500/20 rounded px-3 py-2 text-xs">
                        <p className="text-red-400 font-semibold mb-1">Problems</p>
                        <ul className="list-disc list-inside text-[#ebeef5] space-y-0.5">
                          {r.problems.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                      </div>
                    )}
                    <div className="mt-3">
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70 mb-1">Stages</p>
                      <pre className="bg-[#040b1d] border border-white/5 rounded p-3 text-[11px] text-[#ebeef5] font-mono overflow-x-auto">{JSON.stringify(r.stages, null, 2)}</pre>
                    </div>
                    <div className="mt-3">
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70 mb-1">Counts</p>
                      <pre className="bg-[#040b1d] border border-white/5 rounded p-3 text-[11px] text-[#ebeef5] font-mono overflow-x-auto">{JSON.stringify(r.counts, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Pair({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70">{label}</p>
      <p className="text-[#ebeef5] mt-0.5 truncate">{String(value)}</p>
    </div>
  );
}

function SliceHealthBadge({ health, open, onToggle, onRerun, rerunState }) {
  if (!health) {
    return (
      <div
        className="bg-[#0a1530] border border-white/10 rounded-xl px-5 py-4 flex items-center gap-3"
        data-testid="slice-health-badge-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin text-[#ebeef5]/70" />
        <p className="text-sm text-[#ebeef5]/70">Checking slice health…</p>
      </div>
    );
  }

  if (health.error) {
    return (
      <div
        className="bg-red-500/5 border border-red-500/40 rounded-xl px-5 py-4 flex items-center gap-3"
        data-testid="slice-health-badge-error"
      >
        <AlertTriangle className="h-4 w-4 text-red-400" />
        <p className="text-sm text-red-400">Could not reach /api/health/ready.</p>
      </div>
    );
  }

  const slices = health.slices || {};
  const sliceNames = Object.keys(slices);
  const mountedCount = sliceNames.filter((k) => slices[k]).length;
  const allMounted = !!health.all_slices_mounted;
  const allScoped = !!health.all_tenant_scoped;
  const healthy = allMounted && allScoped;
  const coverage = health.tenant_coverage || {};
  const coverageRows = Object.entries(coverage);
  const unscoped = coverageRows.filter(([, v]) => v && v.present && v.fully_scoped === false);
  const running = !!rerunState?.running;
  const result = rerunState?.result || null;

  return (
    <div
      className={`bg-[#0a1530] border rounded-xl overflow-hidden ${healthy ? "border-white/10" : "border-[#10b7c0]/40"}`}
      data-testid="slice-health-badge"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
      >
        {open ? <ChevronDown className="h-4 w-4 text-[#ebeef5]/70" /> : <ChevronRight className="h-4 w-4 text-[#ebeef5]/70" />}
        {healthy ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-[#10b7c0]" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#f1fcff]">
            Slice health
            <span
              className={`ml-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${
                healthy ? "bg-emerald-500/10 text-emerald-400" : "bg-[#10b7c0]/10 text-[#10b7c0]"
              }`}
              data-testid="slice-health-status-pill"
            >
              {healthy ? "Healthy" : "Attention"}
            </span>
          </p>
          <p className="text-xs text-[#ebeef5]/70 mt-0.5 flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5" data-testid="slice-health-mount-count">
              <Layers className="h-3 w-3" /> {mountedCount}/{sliceNames.length} slices mounted
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="slice-health-scope-status">
              <ShieldCheck className="h-3 w-3" /> {allScoped ? "All collections tenant-scoped" : `${unscoped.length} collection(s) need scoping`}
            </span>
            {health.platform && (
              <span className="text-[#ebeef5]/50">
                {health.platform.organizations} org(s) · {health.platform.owners} owner(s)
              </span>
            )}
          </p>
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/5 pt-4" data-testid="slice-health-detail">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-4">
            {sliceNames.map((name) => {
              const ok = !!slices[name];
              return (
                <div
                  key={name}
                  className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded border ${
                    ok ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-300" : "border-red-500/30 bg-red-500/5 text-red-300"
                  }`}
                  data-testid={`slice-pill-${name}`}
                >
                  {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                  <span className="font-mono">{name}</span>
                </div>
              );
            })}
          </div>
          {coverageRows.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70 mb-1.5">Tenant coverage by collection</p>
              <div className="bg-[#040b1d] border border-white/5 rounded overflow-hidden">
                <table className="w-full text-[11px]" data-testid="tenant-coverage-table">
                  <thead>
                    <tr className="text-[#ebeef5]/50">
                      <th className="text-left font-medium px-3 py-2">Collection</th>
                      <th className="text-right font-medium px-3 py-2">With tenant_id</th>
                      <th className="text-right font-medium px-3 py-2">Total</th>
                      <th className="text-right font-medium px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coverageRows.map(([coll, v]) => {
                      const ok = v && v.fully_scoped;
                      return (
                        <tr
                          key={coll}
                          className="border-t border-white/5"
                          data-testid={`coverage-row-${coll}`}
                        >
                          <td className="px-3 py-1.5 font-mono text-[#ebeef5]">{coll}</td>
                          <td className="px-3 py-1.5 text-right text-[#ebeef5]">{v?.with_tenant_id ?? 0}</td>
                          <td className="px-3 py-1.5 text-right text-[#ebeef5]/70">{v?.total ?? 0}</td>
                          <td className={`px-3 py-1.5 text-right font-semibold ${ok ? "text-emerald-400" : "text-red-400"}`}>
                            {ok ? "✓ scoped" : "✗ gap"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="text-[11px] text-[#ebeef5]/70 max-w-xl">
              <p className="font-semibold text-[#ebeef5] mb-0.5">Re-run migration</p>
              <p>Idempotent. Re-runs the Phase 4 boot hook: additive tenant_id backfill, Phase-5 collection/field renames, then the tenant schema guard. Use when the coverage table above shows a gap — the guard in production aborts startup, so a manual re-run is the fix path.</p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); if (onRerun) onRerun(); }}
              disabled={running}
              className={`shrink-0 text-xs font-semibold px-4 py-2 rounded-md border flex items-center gap-2 transition ${
                running
                  ? "border-white/10 text-[#ebeef5]/50 cursor-not-allowed"
                  : "border-[#10b7c0]/40 text-[#10b7c0] hover:bg-[#10b7c0]/10"
              }`}
              data-testid="slice-health-rerun-btn"
            >
              {running
                ? (<><Loader2 className="h-3 w-3 animate-spin" /> Running…</>)
                : (<><PlayCircle className="h-3 w-3" /> Re-run migration</>)}
            </button>
          </div>

          {result && (
            <div
              className={`mt-3 rounded px-3 py-2 text-xs border ${
                result.ok
                  ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300"
                  : "border-red-500/40 bg-red-500/5 text-red-300"
              }`}
              data-testid="slice-health-rerun-result"
            >
              {result.ok ? (
                <>
                  <p className="font-semibold mb-1">✓ Migration re-run succeeded.</p>
                  <p className="text-[#ebeef5]/70">
                    tenant_id_backfilled: <span className="text-emerald-300 font-mono">{result.data?.counts?.tenant_id_backfilled ?? 0}</span>
                    {" · "}
                    verify_problems: <span className={`font-mono ${result.data?.verify_problems ? "text-red-300" : "text-emerald-300"}`}>{result.data?.verify_problems ?? 0}</span>
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold mb-1">
                    ✗ Migration re-run {result.status === 422 ? "tripped the schema guard" : "failed"}
                    {result.status ? ` (HTTP ${result.status})` : ""}
                  </p>
                  <p className="break-all text-[#ebeef5]">{result.error}</p>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
